import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams, Link } from "react-router-dom";
import { NotificationManager } from 'react-notifications';
import { Row, Col, Form, Button, Card, Spinner, Modal } from 'react-bootstrap';
import moment from 'moment';
import { FiFileText, FiPlus, FiX, FiPrinter, FiArrowLeft, FiSave, FiTrash2 } from 'react-icons/fi';
import { FaFileInvoiceDollar } from 'react-icons/fa';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import ZForm from './../components/ZForm';
import SelectCustomer from './../components/SelectCustomer';

export default function Invoice() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const [invoice, setInvoice] = useState({});
    const [lines, setLines] = useState([{}, {}, {}]);
    const [creating, setCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    /**
     * Cargar la factura
     */
    const loadInvoice = (idInvoice) => {
        setIsLoading(true);
        tre.api('GET', '/invoices/' + (idInvoice ? idInvoice : invoice.id), {
            context: context,
            success: (r) => {
                setInvoice(r);
                setLines(r.lines);
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    /**
     * Init: Verifica si es una nueva factura o se carga una existente
     */
    useEffect(() => {
        if (id === 'create') {
            // Nueva factura
            setCreating(true);
            setIsLoading(true);
            setInvoice({
                type: 'BUDGET',
                date: moment().format('YYYY-MM-DD'),
                customer_id: null,
                base_imponible: 0,
                total_iva: 0,
                total: 0,
                iva: 21,
            });

            // Cargar datos predeterminados desde la compañía
            tre.api('GET', '/companies/' + context.idCompany, {
                context: context,
                success: (r) => {
                    setInvoice((prevInvoice) => ({
                        ...prevInvoice,
                        notes: r.invoice_notes,
                        num_invoice: r.num_invoice
                    }));
                    setIsLoading(false);
                },
                error: () => setIsLoading(false)
            });
        } else {
            // Cargar una factura existente
            loadInvoice(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, context]);

    /**
     * Manejar cambios en el formulario
     */
    const handleForm = ({ name, value }) => {
        setInvoice((prevInvoice) => ({
            ...prevInvoice,
            [name]: value
        }));
    };

    /**
     * Manejar cambios en las líneas del formulario
     */
    const handleFormLine = (idxLine, ev) => {
        const newLines = [...lines];
        const { name, value } = ev.target;
        const currLine = { ...newLines[idxLine], [name]: value };

        // Actualizar base imponible al cambiar cantidad o precio
        if (['amount', 'price'].includes(name)) {
            const bi = (currLine.amount * currLine.price).toFixed(2);
            currLine.base_imponible = bi;
        }

        newLines[idxLine] = currLine;
        setLines(newLines);
    };

    /**
     * Recalcular totales
     */
    const recalculateTotals = () => {
        if (lines.length > 0) {
            let totalInvoice = 0; // Con IVA

            lines.forEach(line => {
                if (line.amount > 0 && line.price > 0) {
                    totalInvoice += (line.amount * line.price);
                }
            });

            const totalBI = totalInvoice / (1 + (invoice.iva / 100));
            const totalIVA = totalInvoice - totalBI;

            setInvoice((prevInvoice) => ({
                ...prevInvoice,
                base_imponible: totalBI,
                total_iva: totalIVA,
                total: totalInvoice
            }));
        }
    };

    /**
     * Efectos: Recalcular totales al cambiar el IVA o las líneas
     */
    useEffect(() => {
        recalculateTotals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoice.iva, lines]);

    /**
     * Guardar factura/presupuesto
     */
    const save = () => {
        setIsSaving(true);
        invoice.lines = lines;
        if (creating) {
            tre.api('POST', '/companies/' + context.idCompany + '/invoices', {
                context: context,
                params: invoice,
                success: (r) => {
                    NotificationManager.success('Factura / presupuesto guardado');
                    navigate('/invoice/' + r.id);
                    setInvoice(r);
                    setCreating(false);
                    setIsSaving(false);

                    // Actualizar el número de factura en la compañía
                    tre.api('PUT', '/companies/' + context.idCompany, {
                        context: context,
                        params: { num_invoice: parseInt(invoice.num_invoice) + 1 },
                    });
                },
                error: () => setIsSaving(false)
            });
        } else {
            tre.api('PUT', '/invoices/' + id, {
                context: context,
                params: invoice,
                success: () => {
                    NotificationManager.success('Factura / presupuesto guardado');
                    setIsSaving(false);
                },
                error: () => setIsSaving(false)
            });
        }
    };

    /**
     * Eliminar factura
     */
    const remove = () => {
        setIsDeleting(true);
        tre.api('DELETE', '/invoices/' + id, {
            context: context,
            success: () => {
                NotificationManager.success('Factura / presupuesto eliminada');
                setShowDeleteModal(false);
                navigate('/invoices');
            },
            error: () => setIsDeleting(false)
        });
    };

    /**
     * Eliminar línea
     */
    const deleteLine = (idxLine) => {
        if (lines.length > 1) {
            const newLines = [...lines];
            newLines.splice(idxLine, 1);
            setLines(newLines);
        }
    };

    /**
     * Descargar factura
     */
    const download = () => {
        window.open(tre.url + '/invoices/' + invoice.id + '.pdf');
    };

    // -----| Render |-----
    if (isLoading) {
        return (
            <div className="container py-4" style={{ maxWidth: '900px' }}>
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando {creating ? 'datos...' : 'factura...'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '900px' }}>
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center">
                    <Button
                        variant="link"
                        onClick={() => navigate('/invoices')}
                        className="p-0 me-3"
                        style={{ color: '#5f6368' }}
                    >
                        <FiArrowLeft size={24} />
                    </Button>
                    <div>
                        <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                            <FiFileText className="me-2" style={{ color: '#1a73e8' }} />
                            {creating ? 'Nuevo Documento' : (invoice.type === 'BUDGET' ? 'Presupuesto' : 'Factura')}
                            {invoice.num_invoice && !creating &&
                                <span className="ms-2" style={{ color: '#5f6368', fontSize: '18px' }}>
                                    #{invoice.num_invoice}
                                </span>
                            }
                        </h4>
                        <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                            {creating ? 'Crear nueva factura o presupuesto' : 'Editar documento'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Invoice Info Card */}
            <Card className="mb-4" style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 12px 0 0' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{ width: '40px', height: '40px', backgroundColor: '#e8f0fe', color: '#1a73e8' }}
                        >
                            <FaFileInvoiceDollar size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Datos del Documento</h6>
                            <small className="text-muted">Información general de la factura o presupuesto</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <ZForm
                        elements={[
                            { label: 'Id', md: 2, name: 'id', value: invoice.id || '', disabled: true },
                            {
                                label: 'Tipo',
                                md: 2,
                                name: 'type',
                                value: invoice.type || '',
                                type: 'select',
                                options: [
                                    { label: 'Presupuesto', value: 'BUDGET' },
                                    { label: 'Factura', value: 'INVOICE' },
                                ]
                            },
                            { label: 'Fecha', md: 2, name: 'date', type: 'date', value: invoice.date },
                            {
                                label: 'IVA',
                                md: 2,
                                name: 'iva',
                                value: invoice.iva || '',
                                type: 'select',
                                options: [
                                    { label: '21 %', value: 21 },
                                    { label: '10 %', value: 10 },
                                    { label: '8 %', value: 8 },
                                    { label: '4 %', value: 4 },
                                ]
                            },
                            { label: 'Nº factura', md: 4, name: 'num_invoice', value: invoice.num_invoice || '' },
                            { label: 'Notas', md: 12, name: 'notes', type: 'textarea', value: invoice.notes || '' },
                        ]}
                        onChange={handleForm}
                    />

                    <SelectCustomer
                        name="customer_id"
                        caption="Cliente"
                        value={invoice.customer_id || ''}
                        onChange={handleForm}
                    />
                </Card.Body>
            </Card>

            {/* Invoice Lines Card */}
            <Card className="mb-4" style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 12px 0 0' }}>
                    <div className="d-flex align-items-center">
                        <div
                            className="d-flex align-items-center justify-content-center rounded-circle me-3"
                            style={{ width: '40px', height: '40px', backgroundColor: '#e6f4ea', color: '#34a853' }}
                        >
                            <FiFileText size={18} />
                        </div>
                        <div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Líneas de Factura</h6>
                            <small className="text-muted">Conceptos y precios</small>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="py-2 px-3" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
                        <Row>
                            <Col md={5} style={{ fontWeight: 500, fontSize: '13px', color: '#5f6368' }}>CONCEPTO</Col>
                            <Col md={2} style={{ fontWeight: 500, fontSize: '13px', color: '#5f6368' }}>CANTIDAD</Col>
                            <Col md={2} style={{ fontWeight: 500, fontSize: '13px', color: '#5f6368' }}>PRECIO (IVA)</Col>
                            <Col md={2} style={{ fontWeight: 500, fontSize: '13px', color: '#5f6368' }}>TOTAL (IVA)</Col>
                            <Col md={1}></Col>
                        </Row>
                    </div>
                    {lines.map((line, idxLine) => (
                        <Row key={idxLine} className="mx-0 py-2 px-3 align-items-center" style={{ borderBottom: '1px solid #e9ecef' }}>
                            <Col md={5}>
                                <Form.Control
                                    name="concept"
                                    value={line.concept || ''}
                                    onChange={(ev) => handleFormLine(idxLine, ev)}
                                    placeholder="Descripción del concepto"
                                    style={{ border: 'none', backgroundColor: 'transparent' }}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Control
                                    name="amount"
                                    value={line.amount || ''}
                                    onChange={(ev) => handleFormLine(idxLine, ev)}
                                    placeholder="0"
                                    className="text-end"
                                    style={{ border: 'none', backgroundColor: 'transparent' }}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Control
                                    name="price"
                                    value={line.price || ''}
                                    onChange={(ev) => handleFormLine(idxLine, ev)}
                                    placeholder="0.00"
                                    className="text-end"
                                    style={{ border: 'none', backgroundColor: 'transparent' }}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Control
                                    disabled
                                    value={line.base_imponible ? `${line.base_imponible} €` : ''}
                                    className="text-end fw-bold"
                                    style={{ border: 'none', backgroundColor: 'transparent', color: '#1a73e8' }}
                                />
                            </Col>
                            <Col md={1} className="text-center">
                                <Button
                                    variant="link"
                                    className="p-0"
                                    style={{ color: '#ea4335' }}
                                    onClick={() => deleteLine(idxLine)}
                                >
                                    <FiX size={18} />
                                </Button>
                            </Col>
                        </Row>
                    ))}

                    {lines.length === 0 && (
                        <div className="text-center py-4">
                            <FiFileText size={32} className="mb-2" style={{ color: '#dadce0' }} />
                            <p className="text-muted mb-0">No hay líneas de factura</p>
                        </div>
                    )}
                </Card.Body>
                <Card.Footer style={{ backgroundColor: 'white', borderRadius: '0 0 12px 12px' }}>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setLines([...lines, {}])}
                    >
                        <FiPlus className="me-1" />
                        Añadir línea
                    </Button>
                </Card.Footer>
            </Card>

            {/* Totals and Actions Card */}
            <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col md={7}>
                            <div className="d-flex flex-wrap gap-2">
                                <Button
                                    variant="primary"
                                    onClick={save}
                                    disabled={isSaving}
                                >
                                    {isSaving ? <Spinner size="sm" className="me-2" /> : <FiSave className="me-2" />}
                                    {isSaving ? 'Guardando...' : 'Guardar'}
                                </Button>
                                {!creating && (
                                    <Button
                                        variant="outline-danger"
                                        onClick={() => setShowDeleteModal(true)}
                                        disabled={isDeleting}
                                    >
                                        <FiTrash2 className="me-2" />
                                        Eliminar
                                    </Button>
                                )}
                                {!creating && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={download}
                                    >
                                        <FiPrinter className="me-2" />
                                        Imprimir
                                    </Button>
                                )}
                            </div>
                        </Col>
                        <Col md={5}>
                            <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <Row className="mb-2">
                                    <Col style={{ color: '#5f6368' }}>Base imponible:</Col>
                                    <Col xs="auto" className="text-end" style={{ color: '#202124' }}>
                                        {invoice.base_imponible ? invoice.base_imponible.toFixed(2) : '0.00'} €
                                    </Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col style={{ color: '#5f6368' }}>IVA ({invoice.iva}%):</Col>
                                    <Col xs="auto" className="text-end" style={{ color: '#202124' }}>
                                        {invoice.total_iva ? invoice.total_iva.toFixed(2) : '0.00'} €
                                    </Col>
                                </Row>
                                <Row>
                                    <Col style={{ fontWeight: 600, color: '#202124' }}>Total:</Col>
                                    <Col xs="auto" className="text-end" style={{ fontWeight: 700, fontSize: '18px', color: '#1a73e8' }}>
                                        {invoice.total ? invoice.total.toFixed(2) : '0.00'} €
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: 'none' }}>
                    <Modal.Title style={{ color: '#202124', fontWeight: 500 }}>Confirmar eliminación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">¿Estás seguro de que quieres eliminar este {invoice.type === 'BUDGET' ? 'presupuesto' : 'factura'}?</p>
                    <p className="text-muted small mb-0">Esta acción no se puede deshacer.</p>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: 'none' }}>
                    <Button variant="light" onClick={() => setShowDeleteModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={remove} disabled={isDeleting}>
                        {isDeleting ? <Spinner size="sm" className="me-2" /> : <FiTrash2 className="me-2" />}
                        {isDeleting ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
